import { useEffect, useMemo, useRef, useState } from 'react';
import { LocationControllerApi } from '../api/generated';
import type { LocationResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseLocationsResult {
  locations: LocationResponse[];
  locationsById: Record<string, LocationResponse>;
  locationsByName: Record<string, LocationResponse>;
  isLoading: boolean;
  loadError: string | null;
}

export function useLocations(): UseLocationsResult {
  const [locations, setLocations] = useState<LocationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchLocations = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const api = new LocationControllerApi(createApiConfiguration());
        const response = await api.getLocations();
        setLocations(response.data);
      } catch (e) {
        setLoadError(getErrorMessage(e, 'Failed to load locations'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const locationsById = useMemo(() => {
    const map: Record<string, LocationResponse> = {};
    for (const loc of locations) {
      map[loc.id] = loc;
    }
    return map;
  }, [locations]);

  const locationsByName = useMemo(() => {
    const map: Record<string, LocationResponse> = {};
    for (const loc of locations) {
      map[loc.name] = loc;
    }
    return map;
  }, [locations]);

  return { locations, locationsById, locationsByName, isLoading, loadError };
}
